import { useQuery } from '@apollo/client'
import { SearchableCombobox, TSearchable } from '@/components/base/combobox'
import { DocumentTypeSelectorQuery, SelectedDocumentTypeSelectorQuery } from './__generated__/selector_query'
import { Query, SelectedQuery } from './selector_query'
import { dedupe } from '@/helpers/dedupe'
import { useCombobox } from '@/helpers/useCombobox'
import { DocumentSubjectType } from '@/types'

type DocumentType = SelectedDocumentTypeSelectorQuery['documentTypes']['nodes'][0]

interface Props {
  value: string
  onChange: (value: TSearchable | null) => void
  invalid: boolean
  subject: DocumentSubjectType
}

export const Selector = ({ value, onChange, invalid, subject }: Props) => {
  const { data: selectedData } = useQuery<SelectedDocumentTypeSelectorQuery>(
    SelectedQuery,
    { variables: { id: value || 'blank' } }
  )
  const { data, loading, refetch, fetchMore } = useQuery<DocumentTypeSelectorQuery>(Query, { variables: { first: 10 } })

  const { pageInfo, items } = useCombobox<DocumentType>({
    pageInfo: data?.documentTypes.pageInfo,
    querySelected: selectedData?.documentTypes.nodes[0],
    queryCollection: data?.documentTypes.nodes,
    buildSearchable: item => ({
      id: item.id,
      name: item.name
    }),
    alphabetical: true
  })

  return (
    <SearchableCombobox
      data={items}
      filterFields={['documentTypeFilters']}
      filters={{ subjectEq: subject }}
      includeBlank={false}
      invalid={invalid}
      loading={loading}
      moreToFetch={pageInfo.current?.hasNextPage}
      placeholder="What would you like to generate?"
      refetch={refetch}
      searchField="nameCont"
      value={items.find(i => i.id == value) || { id: '', name: '' }}
      fetchMore={() => fetchMore({
        variables: { after: pageInfo.current?.endCursor },
        updateQuery(previousData, { fetchMoreResult }) {
          if (Object.keys(previousData).length === 0) return fetchMoreResult
          fetchMoreResult.documentTypes.nodes = dedupe([
            ...previousData.documentTypes.nodes,
            ...fetchMoreResult.documentTypes.nodes
          ])

          return { ...fetchMoreResult }
        }
      })}
      onChange={onChange}
    />
  )
}
